import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { ROLE } from "../../../../utils/Constants";
import { getUser } from "../../../../utils/commonfunction";
import type {
  ChatbotReferenceImage,
  ChatbotSessionSummary,
  ChatbotStreamStatus,
} from "../../services/chatbot.service";
import { COPY_FEEDBACK_TIMEOUT_MS, USER_ROLE } from "./chatbotHook.constants";
import { getInitialState, getWidgetStorageKey, persistWidgetState } from "./chatbotStorage";
import type { RestaurantUser, WidgetMessage } from "./types";
import { useChatbotReferenceImages } from "./useChatbotReferenceImages";
import { useChatbotSessions } from "./useChatbotSessions";
import { useChatbotStreaming } from "./useChatbotStreaming";

export interface UseChatbotReturn {
  messages: WidgetMessage[];
  input: string;
  setInput: (value: string) => void;
  isStreaming: boolean;
  streamStatus: ChatbotStreamStatus;
  isLoadingSessions: boolean;
  isLoadingSessionTurns: boolean;
  isUploadingReferenceImages: boolean;
  isSessionListOpen: boolean;
  sessions: ChatbotSessionSummary[];
  referenceImages: ChatbotReferenceImage[];
  referenceImageUploadError: string | null;
  activeSessionId: string | null;
  copiedId: string | null;
  welcomeName: string | null;
  hasUserMessages: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  handleSend: (event: React.FormEvent) => void;
  handleCopy: (id: string, text: string) => void;
  handleSuggestedPrompt: (prompt: string) => void;
  handleOpenSessions: () => void;
  handleToggleSessions: () => void;
  handleSelectSession: (selectedSessionId: string) => Promise<void>;
  handleDeleteSession: (selectedSessionId: string) => Promise<void>;
  refreshSessionTurns: (targetSessionId?: string | null) => Promise<void>;
  handleReferenceImagesSelected: (files: FileList | null) => void;
  handleRemoveReferenceImage: (imageUrl: string) => void;
  handleReset: () => void;
}

export const useChatbot = (): UseChatbotReturn => {
  const { t } = useTranslation();
  const initialState = useMemo(() => getInitialState(), []);
  const [messages, setMessages] = useState<WidgetMessage[]>(initialState.messages);
  const [sessionId, setSessionId] = useState<string | null>(initialState.sessionId);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState<ChatbotStreamStatus>("thinking");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const storageKey = useMemo(getWidgetStorageKey, []);

  const {
    isUploadingReferenceImages,
    referenceImages,
    referenceImageUploadError,
    setReferenceImages,
    setReferenceImageUploadError,
    handleReferenceImagesSelected,
    handleRemoveReferenceImage,
  } = useChatbotReferenceImages({ isStreaming, t });

  const sessionsState = useChatbotSessions({
    sessionId,
    setSessionId,
    setMessages,
    setInput,
    setReferenceImages,
    setReferenceImageUploadError,
    isStreaming,
    t,
  });

  const streamingState = useChatbotStreaming({
    messages,
    sessionId,
    referenceImages,
    isUploadingReferenceImages,
    isStreaming,
    setIsStreaming,
    input,
    setInput,
    setMessages,
    setSessionId,
    setReferenceImages,
    setReferenceImageUploadError,
    setIsSessionListOpen: sessionsState.setIsSessionListOpen,
    streamStatus,
    setStreamStatus,
    loadSessions: sessionsState.loadSessions,
    refreshSessionTurns: sessionsState.refreshSessionTurns,
    t,
  });

  useEffect(() => {
    persistWidgetState(storageKey, { messages, sessionId });
  }, [messages, sessionId, storageKey]);

  useEffect(() => {
    return () => {
      streamingState.abortControllerRef.current?.abort();
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, [streamingState.abortControllerRef]);

  const handleCopy = useCallback((id: string, text: string): void => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedId(null), COPY_FEEDBACK_TIMEOUT_MS);
    });
  }, []);

  const welcomeName = useMemo<string | null>(() => {
    const user = getUser(ROLE.RESTAURANT) as RestaurantUser | null;
    return user?.first_name ?? user?.name?.split(" ")[0] ?? null;
  }, []);

  const hasUserMessages = useMemo(
    () => messages.some((message) => !message.localOnly && message.role === USER_ROLE),
    [messages]
  );

  return {
    messages,
    input,
    setInput,
    isStreaming,
    streamStatus,
    isLoadingSessions: sessionsState.isLoadingSessions,
    isLoadingSessionTurns: sessionsState.isLoadingSessionTurns,
    isUploadingReferenceImages,
    isSessionListOpen: sessionsState.isSessionListOpen,
    sessions: sessionsState.sessions,
    referenceImages,
    referenceImageUploadError,
    activeSessionId: sessionId,
    copiedId,
    welcomeName,
    hasUserMessages,
    messagesEndRef,
    handleSend: streamingState.handleSend,
    handleCopy,
    handleSuggestedPrompt: streamingState.handleSuggestedPrompt,
    handleOpenSessions: sessionsState.handleOpenSessions,
    handleToggleSessions: sessionsState.handleToggleSessions,
    handleSelectSession: sessionsState.handleSelectSession,
    handleDeleteSession: sessionsState.handleDeleteSession,
    refreshSessionTurns: sessionsState.refreshSessionTurns,
    handleReferenceImagesSelected,
    handleRemoveReferenceImage,
    handleReset: streamingState.handleReset,
  };
};
